import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

export default function AdminSettings() {
  const [settings, setSettings] = useState({ 
    title: '', bio: '', contact_email: '', contact_phone: '', 
    hero_image: '', logo_url: '', 
    gallery_image_1: '', gallery_image_2: '', gallery_image_3: '' 
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);

  const fetchSettings = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/public/settings`);
      setSettings(data);
    } catch (e) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings`, settings, { withCredentials: true });
      toast.success('Configuración guardada');
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingField(field);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(`${API_URL}/api/upload`, formData, { 
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, [field]: `${API_URL}${data.url}` }));
      toast.success('Imagen subida con éxito');
    } catch (err) {
      toast.error('Error al subir imagen');
    } finally {
      setUploadingField(null);
    }
  };

  if (loading) return <div>Cargando...</div>;

  return (
    <div className="max-w-4xl mx-auto pb-20">
      <h1 className="text-3xl font-serif text-blue-900 font-bold mb-8">Configuración del Sitio</h1>
      
      <form onSubmit={handleSave} className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-100 space-y-8">
        
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-slate-800">Información General</h2>
          <div className="space-y-2">
            <Label>Nombre del Negocio / Título Principal</Label>
            <Input 
              value={settings.title} 
              onChange={e => setSettings({...settings, title: e.target.value})} 
              className="text-lg"
            />
          </div>
          <div className="space-y-2">
            <Label>Descripción o Bio</Label>
            <Textarea 
              value={settings.bio} 
              onChange={e => setSettings({...settings, bio: e.target.value})} 
              rows={3}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Email de Contacto</Label>
              <Input 
                value={settings.contact_email} 
                onChange={e => setSettings({...settings, contact_email: e.target.value})} 
              />
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input 
                value={settings.contact_phone} 
                onChange={e => setSettings({...settings, contact_phone: e.target.value})} 
              />
            </div>
          </div>
        </div>

        <div className="space-y-6 pt-6 border-t border-slate-100">
          <h2 className="text-xl font-bold text-slate-800">Marca e Imágenes Principales</h2>
          
          <div>
            <Label>Logo del Negocio</Label>
            {settings.logo_url && (
              <div className="p-4 bg-slate-50 rounded-xl mb-4 inline-block">
                <img src={settings.logo_url.startsWith('http') ? settings.logo_url : `${API_URL}${settings.logo_url}`} alt="Logo" className="h-20 w-auto object-contain" />
              </div>
            )}
            <div className="flex items-center gap-4">
              <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'logo_url')} disabled={!!uploadingField} className="max-w-md file:bg-orange-50 file:text-orange-700 file:border-0 file:rounded-lg file:px-4 file:py-2 file:font-semibold hover:file:bg-orange-100 cursor-pointer" />
              {uploadingField === 'logo_url' && <span className="text-sm text-slate-500">Subiendo...</span>}
            </div>
          </div>

          <div>
            <Label>Imagen Principal (Fondo de Portada)</Label>
            {settings.hero_image && (
              <img src={settings.hero_image.startsWith('http') ? settings.hero_image : `${API_URL}${settings.hero_image}`} alt="Hero" className="w-full h-48 object-cover rounded-xl shadow-sm mb-4" />
            )}
            <div className="flex items-center gap-4">
              <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'hero_image')} disabled={!!uploadingField} className="max-w-md file:bg-orange-50 file:text-orange-700 file:border-0 file:rounded-lg file:px-4 file:py-2 file:font-semibold hover:file:bg-orange-100 cursor-pointer" />
              {uploadingField === 'hero_image' && <span className="text-sm text-slate-500">Subiendo...</span>}
            </div>
          </div>
        </div>

        <div className="space-y-6 pt-6 border-t border-slate-100">
          <h2 className="text-xl font-bold text-slate-800">Galería: Nuestra Presentación</h2>
          <p className="text-slate-500 text-sm">Cambia las tres imágenes que aparecen en la sección central de la página principal.</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[1, 2, 3].map((num) => {
              const field = `gallery_image_${num}`;
              const imgUrl = settings[field];
              return (
                <div key={num} className="space-y-3 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <Label>Imagen {num}</Label>
                  {imgUrl ? (
                    <img src={imgUrl.startsWith('http') ? imgUrl : `${API_URL}${imgUrl}`} alt={`Gallery ${num}`} className="w-full h-32 object-cover rounded-lg shadow-sm" />
                  ) : (
                    <div className="w-full h-32 bg-slate-200 rounded-lg flex items-center justify-center text-slate-400 text-sm">Sin imagen</div>
                  )}
                  <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, field)} disabled={!!uploadingField} className="file:text-xs text-xs" />
                  {uploadingField === field && <span className="text-xs text-orange-600 block">Subiendo...</span>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="pt-6">
          <Button type="submit" disabled={saving || !!uploadingField} className="w-full sm:w-auto px-8 bg-blue-900 hover:bg-blue-800 text-white rounded-xl text-lg">
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>

      </form>
    </div>
  );
}
